// Vault key lifecycle for the browser client.
//
// This is the web counterpart of the iOS VaultKeys.swift + VaultKeyService.swift pair, and it must
// stay behaviourally identical to both, because a DEK wrapped on one platform is unwrapped on the
// other. The primitives themselves come from ./crypto-vault, copied verbatim from the Worker.
//
// The contract, in one place:
//   • DEK             AES-256-GCM, 256-bit, generated client-side
//   • ciphertext      base64(nonce(12) ‖ ciphertext ‖ tag(16))
//   • KEK             PBKDF2-SHA256, 100_000 iterations, 32 bytes
//   • KDF inputs      UTF-8 bytes of the secret string and of the salt *string*
//                     (the salt is a base64 string used as text, NOT its decoded bytes)
//   • wrapped DEK     seal(base64(raw DEK bytes)) under the KEK
//   • salt            base64 of 16 random bytes, stored server-side as dek_salt and reused
//   • recovery phrase 8 BIP-39 words joined by '-'
//
// Deviating from any line above locks users out of their own journal, so the parity tests in
// vault.parity.test.ts assert each of these against fixtures shared with the iOS test suite.

import {
  generateDEK as generateDEKKey,
  exportKeyRaw,
  importDEK,
  deriveKeyFromSecret,
  wrapDEK,
  unwrapDEK,
  sealWithKey,
  openWithKey,
} from './crypto-vault';
import { BIP39_WORDS } from './bip39';

export const RECOVERY_WORD_COUNT = 8;
const RECOVERY_SEPARATOR = '-';
const SALT_BYTES = 16;

export {
  generateDEKKey as generateDEK,
  exportKeyRaw,
  importDEK,
  deriveKeyFromSecret,
  wrapDEK,
  unwrapDEK,
  sealWithKey,
  openWithKey,
};

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

/**
 * Import a DEK that cannot be exported back to raw bytes.
 *
 * This is the form we persist between page loads (see vault/dek-store.ts). The browser has no
 * Keychain, so the alternative to persisting something is asking for the password on every reload,
 * which nobody will tolerate. A non-extractable key still lets us encrypt and decrypt, but script
 * running on the page — including injected script — cannot read the key material out to send it
 * somewhere else. That is a meaningful reduction in blast radius for a small cost: changing the
 * password needs an extractable key to re-wrap, so that flow re-unwraps from the server copy using
 * the password the user is already typing.
 */
export async function importDEKNonExtractable(rawB64: string): Promise<CryptoKey> {
  const bin = atob(rawB64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return crypto.subtle.importKey('raw', bytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** Re-import an extractable DEK as non-extractable, for persistence. */
export async function toNonExtractable(dek: CryptoKey): Promise<CryptoKey> {
  return importDEKNonExtractable(await exportKeyRaw(dek));
}

/**
 * Random 16-byte salt, base64-encoded. Mirrors VaultKeys.generateSalt(). Stored server-side as
 * dek_salt and reused on restore so the same KEK is derived from the user's password.
 */
export function generateSalt(): string {
  return toBase64(crypto.getRandomValues(new Uint8Array(SALT_BYTES)));
}

/**
 * A readable recovery phrase: eight BIP-39 words joined by hyphens, e.g. "river-amber-...-cedar".
 * Mirrors VaultKeys.generateRecoveryKey().
 *
 * This is the secret behind recovery_wrapped_dek, which lives server-side, so the phrase has to
 * survive an offline brute force of a stolen database dump: 2048 words = 11 bits each, so 8 words
 * is 2^88. Words come from the platform CSPRNG, and 65536 % 2048 === 0, so reducing a 16-bit draw
 * modulo 2048 is bias-free — the same reduction iOS performs.
 */
export function generateRecoveryPhrase(wordCount: number = RECOVERY_WORD_COUNT): string {
  const n = BIP39_WORDS.length; // 2048
  const raw = crypto.getRandomValues(new Uint8Array(wordCount * 2));
  const picked: string[] = [];
  for (let i = 0; i < wordCount; i++) {
    const value = (raw[i * 2] << 8) | raw[i * 2 + 1];
    picked.push(BIP39_WORDS[value % n]);
  }
  return picked.join(RECOVERY_SEPARATOR);
}

/**
 * Accept a recovery phrase the way a human will actually type it — extra spaces, mixed case, and
 * spaces or commas instead of hyphens — and return it in the exact canonical form iOS produced.
 * Returns null if it is not a plausible phrase, so the caller can say so before doing 100k
 * iterations of PBKDF2 and reporting a generic failure.
 */
export function normalizeRecoveryPhrase(input: string): string | null {
  const words = input
    .toLowerCase()
    .trim()
    .split(/[\s,\-_]+/)
    .filter(Boolean);
  if (words.length !== RECOVERY_WORD_COUNT) return null;
  if (!words.every((w) => BIP39_WORDS.includes(w))) return null;
  return words.join(RECOVERY_SEPARATOR);
}

/** Which words in a typed phrase are not in the BIP-39 list, for a specific error message. */
export function unknownRecoveryWords(input: string): string[] {
  const words = input.toLowerCase().trim().split(/[\s,\-_]+/).filter(Boolean);
  return words.filter((w) => !BIP39_WORDS.includes(w));
}

export interface VaultWraps {
  passwordWrappedDek: string;
  recoveryWrappedDek: string;
  dekSalt: string;
  rawDek: string;
  recoveryPhrase: string;
}

/**
 * Build the three-wrap payload for POST /api/vault/keys. Mirrors VaultKeyService.provisionCore().
 *
 * `passwordSecret` is null for social accounts (Google), where there is no password: the password
 * slot reuses the recovery phrase, exactly as iOS does, so the phrase becomes the only way back in.
 */
export async function provisionVault(passwordSecret: string | null): Promise<VaultWraps> {
  const dek = await generateDEKKey();
  return wrapExistingDEK(dek, passwordSecret);
}

/** Same as provisionVault but for a DEK that already exists (e.g. after importing legacy entries). */
export async function wrapExistingDEK(
  dek: CryptoKey,
  passwordSecret: string | null,
): Promise<VaultWraps> {
  const dekSalt = generateSalt();
  const recoveryPhrase = generateRecoveryPhrase();
  const pwSecret = passwordSecret ?? recoveryPhrase;

  const passwordKEK = await deriveKeyFromSecret(pwSecret, dekSalt);
  const recoveryKEK = await deriveKeyFromSecret(recoveryPhrase, dekSalt);

  return {
    passwordWrappedDek: await wrapDEK(dek, passwordKEK),
    recoveryWrappedDek: await wrapDEK(dek, recoveryKEK),
    dekSalt,
    rawDek: await exportKeyRaw(dek), // sent once over TLS; the server wraps it and discards it
    recoveryPhrase,
  };
}

/**
 * Unwrap the DEK from a server-stored wrap. Mirrors VaultKeyService.restore().
 * Returns null when the secret is wrong — AES-GCM authentication failure is indistinguishable
 * from a corrupt wrap, and both mean "we cannot open this".
 */
export async function unwrapWithSecret(
  wrappedB64: string,
  secret: string,
  dekSalt: string,
): Promise<CryptoKey | null> {
  try {
    const kek = await deriveKeyFromSecret(secret, dekSalt);
    return await unwrapDEK(wrappedB64, kek);
  } catch {
    return null;
  }
}

/** Re-wrap the unchanged DEK under a new password. Entries are never re-encrypted. */
export async function rewrapForNewPassword(
  dek: CryptoKey,
  newPassword: string,
): Promise<{ passwordWrappedDek: string; dekSalt: string }> {
  const dekSalt = generateSalt();
  const kek = await deriveKeyFromSecret(newPassword, dekSalt);
  return { passwordWrappedDek: await wrapDEK(dek, kek), dekSalt };
}
